import { Text } from '#/components/Text/Text';
import { useI18n } from '#/i18n/hooks/useI18n';
import { SearchLoadingSectionLine } from '#/modules/search/components/Loading/SearchLoadingSectionLine';
import {
  useIsVolopSearchDeduplicateEnabled,
  useVolopListingTotal,
} from '#/modules/search/contexts/SearchContext';
import type { SearchType } from '#/modules/search/types/SearchType';
import { LISTING_TYPE_HUMAN_NAME_MAP } from '#/utils/constants/general/listingTypeHumanNameMap';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP } from '#/utils/constants/general/listingTypePluralHumanNameMap';

type Props = {
  count: number | null;
  searchType: SearchType;
  tag?: 'h2' | 'div';
};

const formatNumber = (num: number) =>
  new Intl.NumberFormat('en-US').format(num);

export function SearchResultsHeaderCount({
  count,
  searchType,
  tag = 'h2',
}: Props) {
  const { i18n } = useI18n();
  const isVolopSearchDeduplicateEnabled = useIsVolopSearchDeduplicateEnabled();

  const isDedupedVolopSearch =
    isVolopSearchDeduplicateEnabled && searchType === 'VOLOP';

  // Under dedupe the main query's nbHits is the distinct-org count (via `distinct: 1`).
  // volopListingTotal is the org-count query's non-distinct nbHits — the true, uncapped
  // listing total. `?? null` preserves the skeleton (pre-fetch / failure) path.
  const volopListingTotal = useVolopListingTotal() ?? null;

  const effectiveCount = isDedupedVolopSearch ? volopListingTotal : count;

  if (effectiveCount === null)
    return <SearchLoadingSectionLine width={64} height={20} />;

  const text =
    effectiveCount === 1
      ? i18n('1 %(searchType)s', {
          searchType: LISTING_TYPE_HUMAN_NAME_MAP(i18n)[searchType],
        })
      : i18n('%(count)s %(searchType)s', {
          count: formatNumber(effectiveCount),
          searchType: LISTING_TYPE_PLURAL_HUMAN_NAME_MAP(i18n)[searchType],
        });

  return (
    <Text
      tag={tag}
      styleAs="body"
      display={['block', 'block', 'block', 'inline-block']}
      fontSize="16px!important"
      mb={0}
      qaId="result-count"
      data-result-count={effectiveCount}
    >
      {text}
    </Text>
  );
}
